import { api } from "../api/Middleware";
import { 
  WABOTTY_PUBLIC_URL,
  PUBLIC_WHATSAPP_TOKEN_INFO_URL,
  PUBLIC_WHATSAPP_CONNECT_URL,
  PUBLIC_WHATSAPP_QR_URL,
  PUBLIC_WHATSAPP_STATUS_URL
} from "../api/Urls";

// ====================================================================
// 🌐 API PÚBLICA - QR CODE WHATSAPP
// ====================================================================
// 
// Este serviço gerencia as chamadas para a API pública de conexão WhatsApp
// Permite conexão externa via tokens únicos de 6 dígitos sem autenticação
//
// IMPORTANTE: Estes endpoints são PÚBLICOS (sem autenticação required)
// ====================================================================

// As URLs já estão definidas no Urls.js

/**
 * 1. Obter informações do token público
 * 
 * @param {string} token - Token de 6 dígitos
 * @returns {Object} Informações do agente e validade do token
 */
export const getPublicTokenInfo = async (token) => {
  try {
    const response = await api.get(PUBLIC_WHATSAPP_TOKEN_INFO_URL(token));
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      };
    }
  } catch (error) {
    console.error("Erro ao buscar informações do token:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        success: false,
        error: data.error || "Token não encontrado",
        status: status,
        errorCode: determineErrorCode(status, data)
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500,
      errorCode: "NETWORK_ERROR"
    };
  }
};

/**
 * 2. Conectar conta WhatsApp via token público
 * 
 * @param {string} token - Token de 6 dígitos
 * @param {string} phone - Número do telefone (+5511999999999)
 * @returns {Object} Dados da sessão criada
 */
export const connectPublicWhatsApp = async (token, phone) => {
  try {
    const payload = {
      phone: phone
    };

    const response = await api.post(PUBLIC_WHATSAPP_CONNECT_URL(token), payload);
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      };
    }
  } catch (error) {
    console.error("Erro ao conectar WhatsApp público:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        success: false,
        error: data.error || "Erro na conexão",
        status: status,
        errorCode: determineErrorCode(status, data),
        details: data
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500,
      errorCode: "NETWORK_ERROR"
    };
  }
};

/**
 * 3. Gerar QR Code público
 * 
 * @param {string} token - Token de 6 dígitos
 * @param {string} phone - Número do telefone (+5511999999999)
 * @returns {Object} Dados binários da imagem QR Code ou erro
 */
export const generatePublicQrCode = async (token, phone) => {
  try {
    const payload = {
      phone: phone
    };

    const response = await api.post(PUBLIC_WHATSAPP_QR_URL(token), payload, {
      responseType: 'arraybuffer',
      timeout: 30000 // 30 segundos timeout para QR Code
    });
    
    if (response.status === 200) {
      // Converter ArrayBuffer para Blob para manipulação
      const blob = new Blob([response.data], { type: 'image/png' });
      const imageUrl = URL.createObjectURL(blob);
      
      return {
        success: true,
        data: {
          imageData: response.data,
          imageUrl: imageUrl,
          blob: blob,
          contentType: 'image/png'
        }
      };
    }
  } catch (error) {
    console.error("Erro ao gerar QR Code público:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      // Se data for ArrayBuffer (erro em imagem), converter para texto
      let errorMessage = "Erro na geração do QR Code";
      let errorData = null;
      
      if (data instanceof ArrayBuffer) {
        try {
          const textDecoder = new TextDecoder();
          const errorText = textDecoder.decode(data);
          errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          // Se não conseguir parsear, usar mensagem padrão
          errorMessage = "Erro interno na geração do QR Code";
        }
      } else {
        errorData = data;
        errorMessage = data.error || errorMessage;
      }
      
      return {
        success: false,
        error: errorMessage,
        status: status,
        errorCode: determineErrorCode(status, errorData),
        details: errorData
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500,
      errorCode: "NETWORK_ERROR"
    };
  }
};

/**
 * 4. Verificar status da sessão pública
 * 
 * @param {string} token - Token de 6 dígitos
 * @param {string} phone - Número do telefone
 * @returns {Object} Status atual da sessão
 */
export const checkPublicSessionStatus = async (token, phone) => {
  try {
    const payload = {
      phone: phone
    };

    const response = await api.post(PUBLIC_WHATSAPP_STATUS_URL(token), payload);
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      };
    }
  } catch (error) {
    console.error("Erro ao verificar status da sessão pública:", error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        success: false,
        error: data.error || "Erro ao verificar status",
        status: status,
        errorCode: determineErrorCode(status, data)
      };
    }
    
    return {
      success: false,
      error: "Erro de conexão",
      status: 500,
      errorCode: "NETWORK_ERROR"
    };
  }
};

/**
 * Função auxiliar para determinar código de erro baseado no status HTTP
 * 
 * @param {number} status - Status HTTP
 * @param {Object} data - Dados da resposta
 * @returns {string} Código do erro
 */
const determineErrorCode = (status, data) => {
  switch (status) {
    case 400:
      if (data?.error?.includes("telefone")) {
        return "INVALID_PHONE";
      }
      if (data?.error?.includes("sessão")) {
        return "INVALID_SESSION_STATE";
      }
      return "BAD_REQUEST";
      
    case 404:
      if (data?.error?.includes("token")) {
        return "TOKEN_NOT_FOUND";
      }
      if (data?.error?.includes("expirado")) {
        return "TOKEN_EXPIRED";
      }
      if (data?.error?.includes("usado")) {
        return "TOKEN_ALREADY_USED";
      }
      return "NOT_FOUND";
      
    case 429:
      return "RATE_LIMIT_EXCEEDED";
      
    case 500:
      if (data?.error?.includes("WAHA")) {
        return "WAHA_API_ERROR";
      }
      if (data?.error?.includes("QR")) {
        return "QR_GENERATION_FAILED";
      }
      return "INTERNAL_SERVER_ERROR";
      
    default:
      return "UNKNOWN_ERROR";
  }
};

/**
 * Função auxiliar para formatar número de telefone para API
 * 
 * @param {string} phone - Número com formatação visual
 * @returns {string} Número limpo para API
 */
export const formatPhoneForAPI = (phone) => {
  // Remove todos os caracteres não numéricos
  const cleanPhone = phone.replace(/\D/g, "");
  
  // Se não começar com 55 (código do Brasil), adicionar
  if (!cleanPhone.startsWith("55") && cleanPhone.length >= 10) {
    return `+55${cleanPhone}`;
  }
  
  // Se já tiver código do país, adicionar + se necessário
  if (!phone.startsWith("+")) {
    return `+${cleanPhone}`;
  }
  
  return cleanPhone.replace(/\D/g, "").replace(/^/, "+");
};

/**
 * Função auxiliar para validar formato de token
 * 
 * @param {string} token - Token a ser validado
 * @returns {Object} Resultado da validação
 */
export const validateTokenFormat = (token) => {
  if (!token) {
    return {
      valid: false,
      error: "Token é obrigatório"
    };
  }
  
  // Token deve ter entre 6 e 8 caracteres alfanuméricos
  const tokenRegex = /^[A-Z0-9]{6,8}$/;
  
  if (!tokenRegex.test(token)) {
    return {
      valid: false,
      error: "Token deve conter 6-8 caracteres alfanuméricos em maiúsculo"
    };
  }
  
  return {
    valid: true
  };
};

/**
 * Função auxiliar para validar formato de telefone brasileiro
 * 
 * @param {string} phone - Telefone a ser validado
 * @returns {Object} Resultado da validação
 */
export const validatePhoneFormat = (phone) => {
  if (!phone) {
    return {
      valid: false,
      error: "Telefone é obrigatório"
    };
  }
  
  const cleanPhone = phone.replace(/\D/g, "");
  
  // Telefone brasileiro deve ter 10 ou 11 dígitos (sem código do país)
  // Ou 12/13 dígitos (com código 55 do Brasil)
  if (cleanPhone.length < 10 || cleanPhone.length > 15) {
    return {
      valid: false,
      error: "Telefone deve ter entre 10 e 15 dígitos"
    };
  }
  
  // Validar padrões brasileiros
  const brazilianPatterns = [
    /^(55)?([1-9][1-9])(9\d{8})$/, // Celular com 9 dígitos
    /^(55)?([1-9][1-9])(\d{8})$/   // Fixo com 8 dígitos
  ];
  
  const isValidBrazilian = brazilianPatterns.some(pattern => pattern.test(cleanPhone));
  
  if (!isValidBrazilian) {
    return {
      valid: false,
      error: "Formato de telefone brasileiro inválido"
    };
  }
  
  return {
    valid: true,
    formatted: phone,
  };
};

// ====================================================================
// CONFIGURAÇÕES E CONSTANTES
// ====================================================================

// Estados possíveis da sessão WAHA
export const SESSION_STATES = {
  STARTING: "STARTING",
  SCAN_QR_CODE: "SCAN_QR_CODE", 
  WORKING: "WORKING",
  FAILED: "FAILED"
};

// Estados possíveis do token
export const TOKEN_STATES = {
  ACTIVE: "active",
  USED: "used",
  EXPIRED: "expired",
  INACTIVE: "inactive"
};

// Códigos de erro padronizados
export const ERROR_CODES = {
  TOKEN_NOT_FOUND: "TOKEN_NOT_FOUND",
  TOKEN_EXPIRED: "TOKEN_EXPIRED", 
  TOKEN_ALREADY_USED: "TOKEN_ALREADY_USED",
  INVALID_PHONE: "INVALID_PHONE",
  INVALID_SESSION_STATE: "INVALID_SESSION_STATE",
  RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",
  WAHA_API_ERROR: "WAHA_API_ERROR",
  QR_GENERATION_FAILED: "QR_GENERATION_FAILED",
  NETWORK_ERROR: "NETWORK_ERROR",
  INTERNAL_SERVER_ERROR: "INTERNAL_SERVER_ERROR"
};

// Rate limits (para referência)
export const RATE_LIMITS = {
  REQUESTS_PER_MINUTE: 5,
  TOKEN_EXPIRY_MINUTES: 30
};

export default {
  getPublicTokenInfo,
  connectPublicWhatsApp,
  generatePublicQrCode,
  checkPublicSessionStatus,
  formatPhoneForAPI,
  validateTokenFormat,
  validatePhoneFormat,
  SESSION_STATES,
  TOKEN_STATES,
  ERROR_CODES,
  RATE_LIMITS
};